import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, fmtDate } from "@/lib/ui";
import {
  LayoutDashboard, Users as UsersIcon, CreditCard, Repeat, Loader2, Search,
  Crown, TrendingUp, DollarSign, UserPlus, Check, X, Activity, Eye, MousePointerClick, Clock, MapPin, Coins, Gift, ArrowDownRight, Megaphone,
  Ban, Trash2, RotateCcw, CalendarClock, ShieldAlert, AlertTriangle, Mail, Inbox, Send, CheckCircle2, CornerDownLeft,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "users", label: "Users", icon: UsersIcon },
  { key: "analytics", label: "Analytics", icon: Activity },
  { key: "payments", label: "Payments", icon: CreditCard },
  { key: "credits", label: "Credits", icon: Coins },
  { key: "notice", label: "Notice", icon: Megaphone },
  { key: "messages", label: "Messages", icon: Mail },
  { key: "subscriptions", label: "Subscriptions", icon: Repeat },
  { key: "engagement", label: "Engagement", icon: Eye },
  { key: "data", label: "Map Data", icon: MapPin },
];

const TIERS = [
  { v: "contestant", l: "Contestant" },
  { v: "circuit_pro", l: "Circuit Pro" },
  { v: "team_family", l: "Team & Family" },
];
const tierLabel = (v) => TIERS.find((t) => t.v === v)?.l || null;

const fmtMoney = (n, ccy = "CAD") =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: ccy || "CAD" }).format(Number(n || 0));

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

const StatusPill = ({ status }) => {
  const map = {
    paid: "bg-green-100 text-green-700", completed: "bg-green-100 text-green-700",
    active: "bg-green-100 text-green-700", pending: "bg-amber-100 text-amber-700",
    initiated: "bg-slate-100 text-slate-600", canceled: "bg-red-100 text-red-600",
    past_due: "bg-red-100 text-red-600", trialing: "bg-blue-100 text-blue-700",
  };
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${map[status] || "bg-slate-100 text-slate-600"}`}>{status || "—"}</span>;
};

function MetricCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="rounded-xl border border-[#E4E7EB] p-5" data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent || "bg-[#FBE9EA] text-[#C8000F]"}`}><Icon className="w-4 h-4" /></span>
      </div>
      <div className="font-display text-3xl font-extrabold tracking-tight">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Overview() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/metrics").then((r) => setM(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))); }, []);
  if (!m) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>;
  const proPct = m.total_users ? Math.round((m.pro_users / m.total_users) * 100) : 0;
  return (
    <div className="space-y-4" data-testid="admin-overview">
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <MetricCard icon={UsersIcon} label="Total Users" value={m.total_users} sub={`+${m.new_users_30d} in last 30 days`} accent="bg-[#EAF1FB] text-[#2563EB]" />
      <MetricCard icon={Crown} label="Pro Members" value={m.pro_users} sub={`${proPct}% of users · ${m.free_users} free`} accent="bg-[#FDF1E4] text-[#E68A2E]" />
      <MetricCard icon={TrendingUp} label="MRR" value={fmtMoney(m.mrr, m.currency)} sub={`${m.active_subscriptions} active subscriptions`} accent="bg-[#E9F6EC] text-[#5FA83B]" />
      <MetricCard icon={DollarSign} label="Total Revenue" value={fmtMoney(m.total_revenue, m.currency)} sub={`${m.paid_transactions} paid / ${m.transactions} txns`} />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <MetricCard icon={Coins} label="Credits Outstanding" value={(m.credits_outstanding ?? 0).toLocaleString()} sub="unspent across all wallets" accent="bg-[#FBF4E6] text-[#8A5A00]" />
      <MetricCard icon={ArrowDownRight} label="Credits Sold" value={(m.credits_purchased ?? 0).toLocaleString()} sub={`${m.credit_pack_sales ?? 0} credit-pack sales`} accent="bg-[#E9F6EC] text-[#5FA83B]" />
      <MetricCard icon={Activity} label="Credits Used" value={(m.credits_spent ?? 0).toLocaleString()} sub={`${(m.credits_bonus ?? 0).toLocaleString()} granted as bonus`} accent="bg-[#FBE9EA] text-[#C8000F]" />
      <MetricCard icon={DollarSign} label="Credit Revenue" value={fmtMoney(m.credit_revenue, m.currency)} sub="from credit-pack purchases" accent="bg-[#FDF1E4] text-[#E68A2E]" />
    </div>
    </div>
  );
}

function AddUserDialog({ open, onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "user", is_pro: false, competitor_name: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users", f);
      toast.success(`${data.email} added`);
      onCreated(data);
      setF({ name: "", email: "", password: "", role: "user", is_pro: false, competitor_name: "" });
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const inp = "w-full mt-1 bg-white border border-[#D7DBE0] rounded-lg px-3 py-2.5 text-sm focus:border-[#C8000F] focus:outline-none";
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="admin-add-user-dialog">
        <DialogHeader><DialogTitle className="font-display">Add a user</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block text-sm font-medium">Full name
            <input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="add-user-name" className={inp} placeholder="e.g. Steele DePaoli" />
          </label>
          <label className="block text-sm font-medium">Email
            <input value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="add-user-email" className={inp} placeholder="rider@example.com" type="email" />
          </label>
          <label className="block text-sm font-medium">Temporary password
            <input value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="add-user-password" className={inp} placeholder="At least 6 characters" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm font-medium">Role
              <select value={f.role} onChange={(e) => set("role", e.target.value)} data-testid="add-user-role" className={inp}>
                <option value="user">User</option>
                <option value="reviewer">Reviewer</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <label className="block text-sm font-medium">Competitor name (optional)
              <input value={f.competitor_name} onChange={(e) => set("competitor_name", e.target.value)} data-testid="add-user-competitor" className={inp} placeholder="For standings" />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input type="checkbox" checked={f.is_pro} onChange={(e) => set("is_pro", e.target.checked)} data-testid="add-user-pro" className="w-4 h-4 accent-[#C8000F]" />
            <Crown className="w-4 h-4 text-[#E68A2E]" /> Grant Pro access
          </label>
          <button onClick={submit} disabled={saving} data-testid="add-user-submit"
            className="w-full flex items-center justify-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-bold px-4 py-2.5 rounded-lg disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Create user
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CompDialog({ user, onClose, onSaved }) {
  const [tier, setTier] = useState(user?.subscription_tier || "");
  const [expires, setExpires] = useState(user?.pro_expires_at ? user.pro_expires_at.slice(0, 10) : "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setTier(user?.subscription_tier || ""); setExpires(user?.pro_expires_at ? user.pro_expires_at.slice(0, 10) : ""); }, [user]);
  const save = async (revoke) => {
    setSaving(true);
    try {
      const body = revoke ? { tier: null } : { tier: tier || "contestant", expires_at: expires ? new Date(expires + "T23:59:59").toISOString() : null };
      const { data } = await api.patch(`/admin/users/${user.id}/subscription`, body);
      toast.success(revoke ? "Comp access revoked" : `Granted ${tierLabel(data.subscription_tier)} free`);
      onSaved(user.id, data);
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const inp = "w-full mt-1 bg-white border border-[#D7DBE0] rounded-lg px-3 py-2.5 text-sm focus:border-[#C8000F] focus:outline-none";
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="comp-dialog">
        <DialogHeader><DialogTitle className="font-display">Free access · {user?.name || user?.email}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-500">Grant this user a complimentary subscription at any tier — no payment required. Leave the expiry blank for open-ended access.</p>
          <label className="block text-sm font-medium">Tier
            <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid="comp-tier" className={inp}>
              {TIERS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </label>
          <label className="block text-sm font-medium">Expires on (optional)
            <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="comp-expires" className={inp} />
          </label>
          <div className="flex gap-2 pt-1">
            <button onClick={() => save(false)} disabled={saving} data-testid="comp-save"
              className="flex-1 flex items-center justify-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-bold px-4 py-2.5 rounded-lg disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />} Grant free access
            </button>
            {(user?.comp || user?.is_pro) && (
              <button onClick={() => save(true)} disabled={saving} data-testid="comp-revoke"
                className="flex items-center justify-center gap-2 border border-[#D7DBE0] hover:bg-[#F7F8FA] text-slate-700 text-sm font-semibold px-4 py-2.5 rounded-lg disabled:opacity-50">
                <X className="w-4 h-4" /> Revoke
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteUserDialog({ user, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const go = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success(`Deleted ${user.email}`);
      onDeleted(user.id);
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="delete-user-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2 text-[#C8000F]"><AlertTriangle className="w-5 h-5" /> Delete user</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">This permanently deletes <span className="font-semibold">{user?.name || user?.email}</span> and their credits ledger, magic links and notifications. Payment records are kept for your books. <span className="font-semibold">This cannot be undone.</span></p>
          <label className="block text-sm font-medium">Type <span className="font-mono text-[#C8000F]">DELETE</span> to confirm
            <input autoFocus value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="delete-confirm-input"
              className="w-full mt-1 bg-white border border-[#D7DBE0] rounded-lg px-3 py-2.5 text-sm focus:border-[#C8000F] focus:outline-none" placeholder="DELETE" />
          </label>
          <div className="flex gap-2 pt-1">
            <button onClick={go} disabled={busy || confirmText !== "DELETE"} data-testid="delete-confirm-btn"
              className="flex-1 flex items-center justify-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-bold px-4 py-2.5 rounded-lg disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete permanently
            </button>
            <button onClick={onClose} disabled={busy} data-testid="delete-cancel-btn"
              className="border border-[#D7DBE0] hover:bg-[#F7F8FA] text-slate-700 text-sm font-semibold px-4 py-2.5 rounded-lg">Cancel</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [adding, setAdding] = useState(false);
  const [comp, setComp] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = (search = "") => api.get("/admin/users", { params: { search } }).then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q]);

  const togglePro = async (u) => {
    setBusy(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/pro`, { is_pro: !u.is_pro });
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, is_pro: !u.is_pro } : x)));
      toast.success(`${u.email} ${!u.is_pro ? "granted" : "revoked"} Pro`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const toggleStatus = async (u) => {
    const next = !(u.active === false) ? false : true; // currently active -> suspend
    setBusy(u.id);
    try {
      await api.patch(`/admin/users/${u.id}/status`, { active: next });
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, active: next } : x)));
      toast.success(`${u.email} ${next ? "reactivated" : "suspended"}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const onComped = (id, data) => setUsers((list) => list.map((x) => (x.id === id ? { ...x, ...data } : x)));

  const total = users?.length || 0;
  const suspended = (users || []).filter((u) => u.active === false).length;

  return (
    <div data-testid="admin-users">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email"
            className="w-full bg-white border border-[#D7DBE0] rounded-lg pl-9 pr-3 py-2.5 text-sm focus:border-[#C8000F] focus:outline-none" />
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-500 ml-auto mr-2">
          <span data-testid="admin-users-total"><span className="font-bold text-[#14181F] text-sm">{total}</span> users</span>
          <span data-testid="admin-users-active"><span className="font-bold text-green-600 text-sm">{total - suspended}</span> active</span>
          <span data-testid="admin-users-suspended"><span className="font-bold text-[#C8000F] text-sm">{suspended}</span> suspended</span>
        </div>
        <button onClick={() => setAdding(true)} data-testid="admin-add-user-btn"
          className="shrink-0 flex items-center gap-1.5 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-bold px-4 py-2.5 rounded-lg">
          <UserPlus className="w-4 h-4" /> Add user
        </button>
      </div>
      <AddUserDialog open={adding} onClose={() => setAdding(false)} onCreated={(u) => setUsers((l) => [u, ...(l || [])])} />
      <CompDialog user={comp} onClose={() => setComp(null)} onSaved={onComped} />
      <DeleteUserDialog user={toDelete} onClose={() => setToDelete(null)} onDeleted={(id) => setUsers((l) => l.filter((x) => x.id !== id))} />
      {!users ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div> : (
        <div className="rounded-xl border border-[#E4E7EB] overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[1040px]">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-[#E4E7EB] bg-[#F7F8FA]">
              <th className="px-4 py-3">User</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3">Signed up</th><th className="px-4 py-3">Last login</th>
              <th className="px-4 py-3 text-right">Credits</th><th className="px-4 py-3 text-right">Spent</th>
              <th className="px-4 py-3 text-right">Actions</th></tr></thead>
            <tbody>
              {users.map((u) => {
                const isSuspended = u.active === false;
                const isAdmin = u.role === "admin";
                return (
                <tr key={u.id} className={`border-b border-[#F3F4F6] hover:bg-[#FAFBFC] ${isSuspended ? "bg-[#FEF4F4]" : ""}`} data-testid={`admin-user-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium flex items-center gap-1.5">{u.name || "—"}{isAdmin && <span className="text-[10px] font-bold text-[#C8000F] bg-[#FBE9EA] px-1.5 py-0.5 rounded">ADMIN</span>}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    {isSuspended
                      ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid={`status-${u.id}`}><Ban className="w-3 h-3" /> Suspended</span>
                      : <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700" data-testid={`status-${u.id}`}><Check className="w-3 h-3" /> Active</span>}
                  </td>
                  <td className="px-4 py-3">
                    {u.is_pro ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold">
                        <Crown className="w-3.5 h-3.5 text-[#E68A2E]" />
                        {tierLabel(u.subscription_tier) || "Pro"}{u.comp ? " (comp)" : ""}
                        {u.pro_expires_at && <span className="text-slate-400"> · till {fmtDate(u.pro_expires_at.slice(0, 10))}</span>}
                      </span>
                    ) : <span className="text-slate-300">Free</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDT(u.created_at)}</td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{u.last_login_at ? fmtDT(u.last_login_at) : (u.last_active ? fmtDT(u.last_active) : "—")}</td>
                  <td className="px-4 py-3 text-right font-medium tabular-nums">{u.credits || 0}</td>
                  <td className="px-4 py-3 text-right font-medium">{u.total_spent ? money(u.total_spent) : "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {isAdmin ? (
                      <div className="inline-flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#5FA83B] bg-[#E9F6EC] px-2.5 py-1 rounded-lg" data-testid={`admin-fullaccess-${u.id}`} title="Admins automatically get full, free access — no plan or credits needed">
                          <Crown className="w-3.5 h-3.5" /> Full access
                        </span>
                        {me?.id !== u.id && (
                          <button onClick={() => setToDelete(u)} data-testid={`delete-user-${u.id}`}
                            className="p-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50" title="Delete this admin (you can recreate it afterwards)">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ) : (
                    <div className="inline-flex items-center gap-1.5">
                      <button onClick={() => setComp(u)} data-testid={`comp-btn-${u.id}`}
                        className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-[#D7DBE0] hover:bg-[#F7F8FA] disabled:opacity-40" title="Grant free access">Free</button>
                      <button onClick={() => togglePro(u)} disabled={busy === u.id} data-testid={`toggle-pro-${u.id}`}
                        className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg disabled:opacity-40 ${u.is_pro ? "border border-[#D7DBE0] hover:bg-[#F7F8FA]" : "bg-[#14181F] text-white hover:bg-black"}`} title={u.is_pro ? "Revoke Pro" : "Grant Pro"}>
                        {busy === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : (u.is_pro ? "Revoke" : "Pro")}
                      </button>
                      <button onClick={() => toggleStatus(u)} disabled={busy === u.id} data-testid={`toggle-status-${u.id}`}
                        className={`p-1.5 rounded-lg border disabled:opacity-40 ${isSuspended ? "border-green-300 text-green-700 hover:bg-green-50" : "border-amber-300 text-amber-700 hover:bg-amber-50"}`}
                        title={isSuspended ? "Reactivate" : "Suspend"}>
                        {isSuspended ? <RotateCcw className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                      </button>
                      <button onClick={() => setToDelete(u)} data-testid={`delete-user-${u.id}`}
                        className="p-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-40" title="Delete user">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    )}
                  </td>
                </tr>
                );
              })}
              {!users.length && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No users found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BarList({ rows, color }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="space-y-2">
      {rows.length === 0 && <div className="text-sm text-slate-400">No data yet.</div>}
      {rows.map((r) => (
        <div key={r.name} data-testid={`analytics-row-${r.name}`}>
          <div className="flex items-center justify-between text-xs mb-0.5"><span className="text-slate-600 truncate mr-2">{r.name}</span><span className="font-semibold text-slate-500">{r.count}</span></div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(r.count / max) * 100}%`, background: color }} /></div>
        </div>
      ))}
    </div>
  );
}

function EngagementTab() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [openRider, setOpenRider] = useState(null);
  useEffect(() => { api.get("/admin/engagement").then((r) => setData(r.data?.riders || [])).catch((e) => { toast.error(apiError(e.response?.data?.detail)); setData([]); }); }, []);
  if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>;
  const rows = data.filter((r) => r.rider.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4" data-testid="admin-engagement">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-extrabold">Rider engagement</h2>
          <p className="text-sm text-slate-500">Who's being followed, by whom, and how often fans are watching.</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rider…" data-testid="engagement-search"
            className="rounded-lg border border-slate-200 pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#C8000F]" />
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Rider</th>
              <th className="px-4 py-3 text-right">Followers</th>
              <th className="px-4 py-3 text-right">Profile opens</th>
              <th className="px-4 py-3 text-right">Video plays</th>
              <th className="px-4 py-3 text-right">Videos</th>
              <th className="px-4 py-3 text-right">Who</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`engagement-row-${i}`}>
                <td className="px-4 py-3 font-semibold text-[#14181F]">{r.rider}</td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold">{r.follower_count}</td>
                <td className="px-4 py-3 text-right tabular-nums">{r.opens}</td>
                <td className="px-4 py-3 text-right tabular-nums">{r.plays}</td>
                <td className="px-4 py-3 text-right tabular-nums">{r.videos}</td>
                <td className="px-4 py-3 text-right">
                  {r.follower_count > 0 && (
                    <button onClick={() => setOpenRider(r)} data-testid={`engagement-who-${i}`} className="text-xs font-semibold text-[#C8000F] hover:underline">View</button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">No engagement yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!openRider} onOpenChange={(o) => !o && setOpenRider(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{openRider?.rider} — {openRider?.follower_count} follower{openRider?.follower_count === 1 ? "" : "s"}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
            {(openRider?.followers || []).map((f, i) => (
              <div key={i} className="flex items-center justify-between gap-2 py-2.5" data-testid={`engagement-follower-${i}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#14181F] truncate">{f.name || "—"}</div>
                  <div className="text-xs text-slate-400 truncate">{f.email}</div>
                </div>
                <span className={`shrink-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${f.account_type === "fan" ? "bg-[#E6B35A]/20 text-[#B4801F]" : "bg-slate-100 text-slate-500"}`}>{f.account_type}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => { setData(null); api.get(`/admin/analytics?days=${days}`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))); }, [days]);
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>;
  return (
    <div data-testid="admin-analytics" className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-slate-500">{data.total_events.toLocaleString()} events tracked in the last {data.days} days</div>
        <div className="flex items-center gap-1 bg-[#F1F3F5] rounded-lg p-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`analytics-days-${d}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-md ${days === d ? "bg-white text-[#C8000F] shadow-sm" : "text-slate-500"}`}>{d}d</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MetricCard icon={Activity} label="Active · 24h" value={data.active_1d} accent="bg-[#E9F6EC] text-[#5FA83B]" />
        <MetricCard icon={Clock} label="Active · 7 days" value={data.active_7d} accent="bg-[#EAF1FB] text-[#2563EB]" />
        <MetricCard icon={Clock} label="Active · 30 days" value={data.active_30d} accent="bg-[#FDF1E4] text-[#E68A2E]" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-[#E4E7EB] p-5">
          <div className="font-display font-bold mb-3 flex items-center gap-2"><Eye className="w-4 h-4 text-[#2563EB]" /> Most-viewed pages</div>
          <BarList rows={data.top_pages} color="#2563EB" />
        </div>
        <div className="rounded-xl border border-[#E4E7EB] p-5">
          <div className="font-display font-bold mb-3 flex items-center gap-2"><MousePointerClick className="w-4 h-4 text-[#C8000F]" /> Most-clicked features</div>
          <BarList rows={data.top_clicks} color="#C8000F" />
        </div>
      </div>
      <div className="rounded-xl border border-[#E4E7EB] overflow-hidden overflow-x-auto">
        <div className="px-4 py-3 font-display font-bold border-b border-[#E4E7EB] bg-[#F7F8FA]">Per-user activity</div>
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-[#E4E7EB]">
            <th className="px-4 py-3">User</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3 text-right">Page views</th>
            <th className="px-4 py-3 text-right">Clicks</th><th className="px-4 py-3">Top pages</th><th className="px-4 py-3">Last active</th></tr></thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.user_id} className="border-b border-[#F3F4F6] hover:bg-[#FAFBFC]" data-testid={`analytics-user-${u.user_id}`}>
                <td className="px-4 py-3 text-slate-600">{u.email || "—"}</td>
                <td className="px-4 py-3">{u.is_pro ? <span className="text-[11px] font-semibold text-[#E68A2E]">Pro</span> : <span className="text-slate-400 text-xs">Free</span>}</td>
                <td className="px-4 py-3 text-right font-medium">{u.page_views}</td>
                <td className="px-4 py-3 text-right font-medium">{u.clicks}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{(u.top_pages || []).map((p) => `${p.name} (${p.count})`).join(", ") || "—"}</td>
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{u.last_active ? fmtDT(u.last_active) : "—"}</td>
              </tr>
            ))}
            {!data.users.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No activity tracked yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PaymentsTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/payments").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))); }, []);
  if (!rows) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>;
  return (
    <div className="rounded-xl border border-[#E4E7EB] overflow-hidden overflow-x-auto" data-testid="admin-payments">
      <table className="w-full text-sm min-w-[760px]">
        <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-[#E4E7EB] bg-[#F7F8FA]">
          <th className="px-4 py-3">Date</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Plan</th>
          <th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3">Status</th></tr></thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.session_id} className="border-b border-[#F3F4F6] hover:bg-[#FAFBFC]" data-testid={`payment-${t.session_id}`}>
              <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDT(t.created_at)}</td>
              <td className="px-4 py-3"><div className="font-medium">{t.user_name}</div><div className="text-xs text-slate-500">{t.user_email}</div></td>
              <td className="px-4 py-3 text-slate-600">{t.lookup_key}</td>
              <td className="px-4 py-3 text-right font-medium">{fmtMoney(t.amount, t.currency)}</td>
              <td className="px-4 py-3"><StatusPill status={t.payment_status} /></td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No transactions yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function SubscriptionsTab() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/admin/subscriptions").then((r) => setRows(r.data)).catch((e) => { setErr(apiError(e.response?.data?.detail)); setRows([]); });
  }, []);
  if (!rows) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>;
  return (
    <div data-testid="admin-subscriptions">
      {err && <div className="mb-4 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-4 py-2.5">Live Stripe data unavailable: {err}</div>}
      <div className="rounded-xl border border-[#E4E7EB] overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-[#E4E7EB] bg-[#F7F8FA]">
            <th className="px-4 py-3">Customer</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3 text-right">MRR</th>
            <th className="px-4 py-3">Renews</th><th className="px-4 py-3">Status</th></tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-b border-[#F3F4F6] hover:bg-[#FAFBFC]" data-testid={`sub-${s.id}`}>
                <td className="px-4 py-3 font-medium">{s.customer_email}</td>
                <td className="px-4 py-3 text-slate-600">{fmtMoney(s.amount, s.currency)}/{s.interval}</td>
                <td className="px-4 py-3 text-right font-medium">{fmtMoney(s.mrr, s.currency)}</td>
                <td className="px-4 py-3 text-slate-500">{s.current_period_end ? new Date(s.current_period_end * 1000).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "—"}{s.cancel_at_period_end && <span className="ml-1 text-[10px] text-red-500">(cancels)</span>}</td>
                <td className="px-4 py-3"><StatusPill status={s.status} /></td>
              </tr>
            ))}
            {!rows.length && !err && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No subscriptions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CreditsTab() {
  const [data, setData] = useState(null);
  const PLAN_LABEL = { rookie: "Rookie", semi_pro: "Semi Pro", professional: "Professional", fan: "Fan", plus: "Plus", all_access: "All Access" };
  useEffect(() => { api.get("/admin/credits").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))); }, []);
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>;
  const kindPill = (k) => {
    const map = { purchase: ["bg-green-100 text-green-700", ArrowDownRight], bonus: ["bg-amber-100 text-amber-700", Gift], spend: ["bg-red-100 text-red-600", Activity] };
    const [cls, Ic] = map[k] || map.spend;
    return <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}><Ic className="w-3 h-3" /> {k}</span>;
  };
  return (
    <div data-testid="admin-credits" className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {Object.entries(data.plan_counts || {}).map(([plan, n]) => (
          <span key={plan} data-testid={`plan-count-${plan}`} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#F1F3F5] text-slate-700 px-3 py-1.5 rounded-full">
            <Crown className="w-3.5 h-3.5 text-[#E68A2E]" /> {PLAN_LABEL[plan] || plan}: {n}
          </span>
        ))}
      </div>

      <div className="rounded-xl border border-[#E4E7EB] overflow-hidden overflow-x-auto">
        <div className="px-4 py-3 font-display font-bold border-b border-[#E4E7EB] bg-[#F7F8FA]">Per-user credits</div>
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-[#E4E7EB]">
            <th className="px-4 py-3">User</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3 text-right">Balance</th>
            <th className="px-4 py-3 text-right">Purchased</th><th className="px-4 py-3 text-right">Used</th><th className="px-4 py-3 text-right">Bonus</th></tr></thead>
          <tbody>
            {data.users.map((u, i) => (
              <tr key={i} className="border-b border-[#F3F4F6] hover:bg-[#FAFBFC]" data-testid={`credits-user-${i}`}>
                <td className="px-4 py-3"><div className="font-medium">{u.name || "—"}</div><div className="text-xs text-slate-500">{u.email}</div></td>
                <td className="px-4 py-3 text-slate-600">{PLAN_LABEL[u.plan] || u.plan}</td>
                <td className="px-4 py-3 text-right font-display font-bold text-[#8A5A00]">{u.credits.toLocaleString()}</td>
                <td className="px-4 py-3 text-right text-green-700 font-medium">{u.purchased ? `+${u.purchased}` : "—"}</td>
                <td className="px-4 py-3 text-right text-[#C8000F] font-medium">{u.spent ? `-${u.spent}` : "—"}</td>
                <td className="px-4 py-3 text-right text-slate-500">{u.bonus || "—"}</td>
              </tr>
            ))}
            {!data.users.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No credit activity yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="rounded-xl border border-[#E4E7EB] overflow-hidden overflow-x-auto">
        <div className="px-4 py-3 font-display font-bold border-b border-[#E4E7EB] bg-[#F7F8FA]">Recent activity</div>
        <table className="w-full text-sm min-w-[680px]">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-[#E4E7EB]">
            <th className="px-4 py-3">Date</th><th className="px-4 py-3">User</th><th className="px-4 py-3">Type</th>
            <th className="px-4 py-3">Reason</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3 text-right">Balance</th></tr></thead>
          <tbody>
            {data.recent.map((e, i) => (
              <tr key={i} className="border-b border-[#F3F4F6] hover:bg-[#FAFBFC]" data-testid={`credits-ledger-${i}`}>
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDT(e.created_at)}</td>
                <td className="px-4 py-3 text-slate-600">{e.email}</td>
                <td className="px-4 py-3">{kindPill(e.kind)}</td>
                <td className="px-4 py-3 text-slate-600">{e.reason}</td>
                <td className={`px-4 py-3 text-right font-semibold ${(e.amount || 0) < 0 ? "text-[#C8000F]" : "text-green-700"}`}>{(e.amount || 0) > 0 ? "+" : ""}{e.amount}</td>
                <td className="px-4 py-3 text-right text-slate-500">{e.balance_after}</td>
              </tr>
            ))}
            {!data.recent.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No ledger entries yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NoticeTab() {
  const [a, setA] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/announcement").then((r) => setA(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))); }, []);
  if (!a) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>;
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/announcement", { title: a.title || "PRCA Notice", body: a.body || "", active: !!a.active }); toast.success("Notice saved — it's live for all athletes"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  return (
    <div data-testid="admin-notice" className="max-w-2xl space-y-4">
      <div className="rounded-xl border border-[#E6B35A]/50 bg-[#FBF4E6] p-4 flex items-start gap-3">
        <Megaphone className="w-5 h-5 text-[#B4801F] shrink-0 mt-0.5" />
        <div className="text-sm text-[#5A4100]">Paste the current PRCA "yellow flag" notice here. When active, it shows to every athlete as a Today banner and a 🔔 notification. Editing it re-alerts everyone.</div>
      </div>
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Title</label>
        <input data-testid="notice-title" value={a.title || ""} onChange={(e) => setA({ ...a, title: e.target.value })}
          className="mt-1 w-full border border-[#D7DBE0] rounded-lg px-3 py-2 text-sm" placeholder="PRCA Notice" />
      </div>
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Notice text</label>
        <textarea data-testid="notice-body" value={a.body || ""} onChange={(e) => setA({ ...a, body: e.target.value })} rows={8}
          className="mt-1 w-full border border-[#D7DBE0] rounded-lg px-3 py-2 text-sm leading-relaxed" placeholder="Attention Timed Event Contestants: …" />
      </div>
      <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
        <input type="checkbox" data-testid="notice-active" checked={!!a.active} onChange={(e) => setA({ ...a, active: e.target.checked })} className="w-4 h-4 accent-[#C8000F]" />
        Show this notice to athletes
      </label>
      <button onClick={save} disabled={saving} data-testid="notice-save"
        className="inline-flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save notice
      </button>
      {a.updated_at && <p className="text-xs text-slate-400">Last updated {fmtDT(a.updated_at)}</p>}
    </div>
  );
}

function DataTab() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/geocode-backfill/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!st?.running) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [st?.running]);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/geocode-backfill");
      if (!data.started) toast.message(data.reason ? `Already running (${data.reason})` : "Already running");
      else toast.success(`Geocoding ${data.total} rodeos…`);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const running = st?.running;
  const pct = st?.total ? Math.round((st.done / st.total) * 100) : 0;
  return (
    <div data-testid="admin-data" className="max-w-xl">
      <div className="rounded-xl border border-[#E4E7EB] p-5">
        <div className="font-display font-bold text-lg flex items-center gap-2 mb-1"><MapPin className="w-5 h-5 text-[#C8000F]" /> Map coordinates</div>
        <p className="text-sm text-slate-500 mb-4">Rodeos need GPS coordinates to appear as pins and to fly-to when tapped on the map. This now runs <strong>automatically</strong> whenever the server restarts if any are still missing, and resumes where it left off — you shouldn't need to babysit it. Use the button below to force a run (uses Open-Meteo — free).</p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <MetricCard icon={Check} label="With coordinates" value={st?.with_coords_now ?? "—"} accent="bg-[#E9F6EC] text-[#5FA83B]" />
          <MetricCard icon={X} label="Still missing" value={st?.missing_now ?? "—"} accent="bg-[#FBE9EA] text-[#C8000F]" />
        </div>
        {st?.last_run && !running && (
          <p className="text-xs text-slate-400 mb-3" data-testid="geocode-last-run">Last run: {new Date(st.last_run).toLocaleString()}</p>
        )}
        {running && (
          <div className="mb-4" data-testid="geocode-progress">
            <div className="flex justify-between text-xs text-slate-500 mb-1"><span>Geocoding… {st.done}/{st.total}</span><span>{pct}%</span></div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-[#C8000F] transition-all" style={{ width: `${pct}%` }} /></div>
          </div>
        )}
        <button onClick={run} disabled={busy || running} data-testid="geocode-backfill-btn"
          className="w-full flex items-center justify-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-bold px-4 py-3 rounded-lg disabled:opacity-50">
          {(busy || running) ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
          {running ? "Geocoding in progress…" : "Geocode missing rodeos"}
        </button>
      </div>
    </div>
  );
}

function MessagesTab() {
  const [data, setData] = useState(null);
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const load = () => api.get("/admin/contact-messages").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  const markRead = async (id) => {
    try { await api.patch(`/admin/contact-messages/${id}/read`); setData((d) => ({ ...d, items: d.items.map((m) => m.id === id ? { ...m, read: true } : m), unread: Math.max(0, d.unread - 1) })); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try { await api.delete(`/admin/contact-messages/${id}`); setData((d) => ({ ...d, items: d.items.filter((m) => m.id !== id) })); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openReply = (m) => { setReplyingId(m.id); setReplyText(m.replied && m.reply ? m.reply : ""); };
  const sendReply = async (id) => {
    const text = replyText.trim();
    if (text.length < 2) { toast.error("Write a reply first."); return; }
    setSending(true);
    try {
      const { data: res } = await api.post(`/admin/contact-messages/${id}/reply`, { reply: text });
      toast.success("Reply sent by email");
      setData((d) => ({ ...d, items: d.items.map((m) => m.id === id ? { ...m, replied: true, reply: text, replied_at: res.replied_at, read: true } : m) }));
      setReplyingId(null); setReplyText("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>;
  return (
    <div data-testid="admin-messages" className="max-w-3xl">
      <div className="flex items-center gap-2 mb-4 text-sm text-slate-500">
        <Inbox className="w-4 h-4 text-[#C8000F]" />
        <span><b className="text-[#14181F]">{data.items.length}</b> messages · <b className="text-[#C8000F]">{data.unread}</b> unread</span>
      </div>
      {data.items.length === 0 ? (
        <div className="rounded-xl border border-[#E4E7EB] p-10 text-center text-slate-400">No messages yet. Submissions from the Contact page land here.</div>
      ) : (
        <div className="space-y-2.5">
          {data.items.map((m) => (
            <div key={m.id} data-testid={`contact-msg-${m.id}`}
              className={`rounded-xl border p-4 ${m.read ? "border-[#E4E7EB] bg-white" : "border-[#C8000F]/40 bg-[#FDF2F3]"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-bold text-sm flex items-center gap-2 flex-wrap">
                    {!m.read && <span className="w-2 h-2 rounded-full bg-[#C8000F] shrink-0" />}
                    {m.subject || "General enquiry"}
                    {m.replied && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#3B6B1E] bg-[#3B6B1E]/10 rounded-full px-2 py-0.5"><CheckCircle2 className="w-3 h-3" /> Replied</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{m.name} · <a href={`mailto:${m.email}`} className="text-[#1D4ED8] hover:underline">{m.email}</a> · {fmtDT(m.created_at)}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {!m.read && <button onClick={() => markRead(m.id)} data-testid={`msg-read-${m.id}`} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-[#D7DBE0] hover:bg-[#F7F8FA]">Mark read</button>}
                  <button onClick={() => (replyingId === m.id ? setReplyingId(null) : openReply(m))} data-testid={`msg-reply-${m.id}`} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-[#14181F] text-white hover:bg-black flex items-center gap-1.5"><CornerDownLeft className="w-3.5 h-3.5" /> {m.replied ? "Reply again" : "Reply"}</button>
                  <button onClick={() => del(m.id)} data-testid={`msg-del-${m.id}`} className="p-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="text-sm text-slate-700 mt-2.5 whitespace-pre-wrap leading-relaxed">{m.message}</p>
              {m.replied && m.reply && replyingId !== m.id && (
                <div className="mt-3 rounded-lg bg-[#F4F5F7] border-l-[3px] border-[#C8000F] px-3 py-2" data-testid={`msg-reply-sent-${m.id}`}>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Your reply{m.replied_at ? ` · ${fmtDT(m.replied_at)}` : ""}</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{m.reply}</p>
                </div>
              )}
              {replyingId === m.id && (
                <div className="mt-3 border-t border-[#E4E7EB] pt-3" data-testid={`msg-reply-box-${m.id}`}>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={4}
                    autoFocus
                    placeholder={`Write a reply to ${m.name || "this rider"}… (sent to ${m.email} by email)`}
                    data-testid={`msg-reply-input-${m.id}`}
                    className="w-full rounded-lg border border-[#D7DBE0] p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8000F]/40 resize-y"
                  />
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button onClick={() => { setReplyingId(null); setReplyText(""); }} className="text-xs font-semibold px-3 py-2 rounded-lg border border-[#D7DBE0] hover:bg-[#F7F8FA]">Cancel</button>
                    <button onClick={() => sendReply(m.id)} disabled={sending} data-testid={`msg-reply-send-${m.id}`} className="text-xs font-semibold px-3.5 py-2 rounded-lg bg-[#C8000F] text-white hover:bg-[#a60009] disabled:opacity-60 flex items-center gap-1.5">
                      {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send reply
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="px-4 sm:px-6 py-6">
      <div className="flex items-center gap-2 mb-6">
        <LayoutDashboard className="w-6 h-6 text-[#C8000F]" />
        <h1 className="font-display text-2xl font-extrabold tracking-tight">Admin Dashboard</h1>
      </div>
      <div className="flex items-center gap-1 mb-6 border-b border-[#E4E7EB] overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === t.key ? "border-[#C8000F] text-[#C8000F]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "overview" && <Overview />}
      {tab === "users" && <UsersTab />}
      {tab === "analytics" && <AnalyticsTab />}
      {tab === "payments" && <PaymentsTab />}
      {tab === "credits" && <CreditsTab />}
      {tab === "notice" && <NoticeTab />}
      {tab === "messages" && <MessagesTab />}
      {tab === "subscriptions" && <SubscriptionsTab />}
      {tab === "engagement" && <EngagementTab />}
      {tab === "data" && <DataTab />}
    </div>
  );
}
